import { UserRole, TalentMetrics, DashboardCard, Employee, AIRecommendation } from '../types';

// Audit logging for sensitive actions
interface AuditLog {
  userId: string;
  action: string;
  resource: string;
  timestamp: Date;
  details?: any;
}

class AuditLogger {
  private logs: AuditLog[] = [];

  log(userId: string, action: string, resource: string, details?: any): void {
    const auditEntry: AuditLog = {
      userId,
      action,
      resource,
      timestamp: new Date(),
      details
    };
    
    this.logs.push(auditEntry);
    
    // In production, send to audit service
    console.log('AUDIT:', auditEntry);
  }

  getLogs(): AuditLog[] {
    return [...this.logs];
  }
}

export const auditLogger = new AuditLogger();

// Define permissions for each role
export const ROLE_PERMISSIONS = {
  HR_LEADER: {
    canViewAllEmployees: true,
    canViewSensitiveMetrics: true,
    canViewAIInsights: true,
    canExportData: true,
    canManageUsers: true,
    canViewCompensationData: true,
    canViewAttritionPredictions: true,
    canViewRecruitmentData: true,
    canManageRequisitions: true,
    canViewAllCandidates: true,
    departmentAccess: 'all', // 'all' | 'own' | 'none'
    metricsAccess: 'full', // 'full' | 'limited' | 'none'
  },
  MANAGER: {
    canViewAllEmployees: false,
    canViewSensitiveMetrics: false,
    canViewAIInsights: false,
    canExportData: false,
    canManageUsers: false,
    canViewCompensationData: false,
    canViewAttritionPredictions: false,
    canViewRecruitmentData: false,
    canManageRequisitions: false,
    canViewAllCandidates: false,
    departmentAccess: 'own', // Only their own department/team
    metricsAccess: 'limited', // Limited metrics only
  },
  RECRUITER: {
    canViewAllEmployees: false,
    canViewSensitiveMetrics: false,
    canViewAIInsights: false,
    canExportData: true,
    canManageUsers: false,
    canViewCompensationData: true, // For offer management
    canViewAttritionPredictions: false,
    canViewRecruitmentData: true,
    canManageRequisitions: true,
    canViewAllCandidates: false, // Only assigned candidates
    departmentAccess: 'assigned', // Only assigned requisitions
    metricsAccess: 'recruitment', // Recruitment metrics only
  }
} as const;

// Get permissions for a specific role
export const getPermissions = (role: UserRole) => {
  return ROLE_PERMISSIONS[role];
};

// Check if user has specific permission
export const hasPermission = (role: UserRole, permission: keyof typeof ROLE_PERMISSIONS.HR_LEADER): boolean => {
  return ROLE_PERMISSIONS[role][permission] as boolean;
};

// Filter dashboard cards based on role
export const filterDashboardCards = (cards: DashboardCard[], role: UserRole): DashboardCard[] => {
  const permissions = getPermissions(role);
  
  if (permissions.metricsAccess === 'full') {
    return cards; // HR Leaders see all cards
  }
  
  // Managers only see basic metrics, filtered list
  const allowedCardIds = ['1', '3', '5']; // Total Employees, Engagement Score, Time to Fill
  return cards.filter(card => allowedCardIds.includes(card.id));
};

// Filter talent metrics based on role
export const filterTalentMetrics = (metrics: TalentMetrics, role: UserRole): TalentMetrics => {
  const permissions = getPermissions(role);
  
  if (permissions.metricsAccess === 'full') {
    return metrics; // HR Leaders see all metrics
  }
  
  // Managers see limited metrics with some data hidden/anonymized
  return {
    ...metrics,
    attritionRate: 0, // Hide sensitive attrition data
    leadershipReadiness: 0, // Hide leadership pipeline data
    diversityScore: 0, // Hide diversity metrics
    hiringTrend: 0, // Hide hiring trends
  };
};

// Data masking utilities
export const maskSensitiveData = (value: any, role: UserRole, dataType: 'salary' | 'performance' | 'email' | 'name'): any => {
  const permissions = getPermissions(role);
  
  if (permissions.canViewSensitiveMetrics || permissions.canViewCompensationData) {
    return value; // Full access for authorized roles
  }
  
  switch (dataType) {
    case 'salary':
      return typeof value === 'number' ? '***' : value;
    case 'performance':
      return typeof value === 'number' ? Math.round(value / 10) * 10 : value; // Round to nearest 10
    case 'email':
      return typeof value === 'string' ? value.replace(/(.{2})(.*)(@.*)/, '$1***$3') : value;
    case 'name':
      return typeof value === 'string' ? value.split(' ').map((part, i) => i === 0 ? part : part.charAt(0) + '***').join(' ') : value;
    default:
      return value;
  }
};

// Enhanced employee filtering with data masking
export const filterEmployees = (employees: Employee[], role: UserRole, userDepartment?: string, userId?: string): Employee[] => {
  const permissions = getPermissions(role);
  
  let filteredEmployees = employees;
  
  // Apply role-based filtering
  if (permissions.canViewAllEmployees) {
    filteredEmployees = employees; // HR Leaders see all employees
  } else if (permissions.departmentAccess === 'own' && userDepartment) {
    // Managers only see employees from their department
    filteredEmployees = employees.filter(employee => employee.department === userDepartment);
  } else if (permissions.departmentAccess === 'assigned' && userId) {
    // Recruiters only see employees for their assigned requisitions
    filteredEmployees = employees.filter(employee => 
      employee.recruiterId === userId || employee.hiringManager === userId
    );
  } else {
    // No access for other roles
    return [];
  }
  
  // Apply data masking based on permissions
  return filteredEmployees.map(employee => {
    if (permissions.canViewSensitiveMetrics) {
      return employee; // Full data access
    }
    
    // Apply data masking for limited access
    return {
      ...employee,
      salary: maskSensitiveData(employee.salary, role, 'salary'),
      performanceScore: maskSensitiveData(employee.performanceScore, role, 'performance'),
      email: permissions.canViewCompensationData ? employee.email : maskSensitiveData(employee.email, role, 'email'),
      // Hide sensitive fields for non-privileged users
      potentialRating: permissions.canViewAIInsights ? employee.potentialRating : undefined,
      attritionRisk: permissions.canViewAIInsights ? employee.attritionRisk : 0,
      leadershipPotential: permissions.canViewAIInsights ? employee.leadershipPotential : 0,
    };
  });
};

// Filter AI recommendations based on role
export const filterAIRecommendations = (recommendations: AIRecommendation[], role: UserRole): AIRecommendation[] => {
  const permissions = getPermissions(role);
  
  if (permissions.canViewAIInsights) {
    return recommendations; // HR Leaders see all AI insights
  }
  
  return []; // Managers don't see AI recommendations
};

// Get role-specific navigation items
export const getRoleBasedNavigation = (role: UserRole) => {
  const permissions = getPermissions(role);
  
  const baseNavigation = [
    { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
  ];
  
  const hrLeaderNavigation = [
    { path: '/profiles', label: 'Employee Profiles', icon: 'people' },
    { path: '/insights', label: 'Talent Insights', icon: 'analytics' },
    { path: '/employee-analytics', label: 'Employee Analytics', icon: 'table_chart' },
    { path: '/belonging', label: 'Belonging Hub', icon: 'favorite' },
    { path: '/admin', label: 'Administration', icon: 'settings' },
  ];
  
  const recruiterNavigation = [
    { path: '/requisitions', label: 'My Requisitions', icon: 'briefcase' },
    { path: '/candidates', label: 'Candidate Pipeline', icon: 'people' },
    { path: '/market-intelligence', label: 'Market Intelligence', icon: 'trending' },
    { path: '/belonging', label: 'Belonging Hub', icon: 'favorite' },
  ];
  
  const managerNavigation = [
    { path: '/profiles', label: 'My Team', icon: 'people' },
    { path: '/belonging', label: 'Belonging Hub', icon: 'favorite' },
  ];
  
  if (role === 'HR_LEADER') {
    return [...baseNavigation, ...hrLeaderNavigation];
  } else if (role === 'RECRUITER') {
    return [...baseNavigation, ...recruiterNavigation];
  } else if (role === 'MANAGER') {
    return [...baseNavigation, ...managerNavigation];
  }
  
  return baseNavigation;
};

// Get role-specific dashboard config
export const getRoleDashboardConfig = (role: UserRole) => {
  const permissions = getPermissions(role);
  
  return {
    showAIInsights: permissions.canViewAIInsights,
    showSensitiveMetrics: permissions.canViewSensitiveMetrics,
    showExportOptions: permissions.canExportData,
    showAdvancedFilters: permissions.canViewAllEmployees,
    allowDataDrilldown: permissions.metricsAccess === 'full',
    maxEmployeesVisible: permissions.canViewAllEmployees ? -1 : 50, // -1 for unlimited
  };
};

// Role-specific welcome messages
export const getRoleWelcomeMessage = (role: UserRole, userName: string): string => {
  switch (role) {
    case 'HR_LEADER':
      return `Welcome back, ${userName}. You have full access to HR analytics and insights.`;
    case 'MANAGER':
      return `Welcome back, ${userName}. View your team's performance and development insights.`;
    case 'RECRUITER':
      return `Welcome back, ${userName}. Manage your requisitions and candidate pipeline.`;
    default:
      return `Welcome back, ${userName}.`;
  }
};

// Get role display name
export const getRoleDisplayName = (role: UserRole): string => {
  switch (role) {
    case 'HR_LEADER':
      return 'HR Leader';
    case 'MANAGER':
      return 'Manager';
    case 'RECRUITER':
      return 'Recruiter';
    default:
      return 'User';
  }
};